/**
 * Welcher Leistungserbringer im Portal gerade gewaehlt ist (Haus-Wechsel, Backend `docs/claude/haus-wechsel.md`).
 *
 * Die Wahl gilt JE TAB (`sessionStorage`): ein Tab, der in Haus A eine Akte zeigt, darf nicht still
 * nach Haus B umschalten, weil in einem anderen Tab gewechselt wurde. Seine naechsten Schreibzugriffe
 * landeten sonst mit Ids aus A im Haus B. Ein NEUER Tab startet mit der zuletzt getroffenen Wahl
 * (`localStorage`), sonst fiele jeder Link aus einer Mail in das Heimat-Haus zurueck.
 *
 * Bewusst reine Funktionen ohne Angular: der Interceptor braucht die Wahl synchron bei jedem Request.
 * Jeder Zugriff auf den Speicher steht in try/catch, weil Safari im privaten Modus und gesperrte
 * Website-Daten dort werfen. Dann gibt es eben keine Wahl, und das Portal arbeitet im Heimat-Haus.
 */

/** Request-Header mit dem gewaehlten Haus. Muss zu `HausWahl.HEADER` im Backend passen. */
export const HAUS_HEADER = 'X-Sanitaetshaus'
/** Antwort-Header des Backends, wenn das Konto das gewaehlte Haus nicht (mehr) betreten darf. */
export const HAUS_ABGELEHNT_HEADER = 'X-Haus-Abgelehnt'

const SCHLUESSEL_TAB = 'saniup.haus-wahl'
const SCHLUESSEL_ZULETZT = 'saniup.haus-wahl.zuletzt'
const SCHLUESSEL_VERLAUF = 'saniup.haus-wahl.verlauf'
const SCHLUESSEL_KONTO = 'saniup.haus-wahl.konto'
const VERLAUF_LAENGE = 5

function lies(speicher: () => Storage, schluessel: string): string | null {
  try {
    return speicher().getItem(schluessel)
  } catch {
    return null
  }
}

function schreib(speicher: () => Storage, schluessel: string, wert: string | null): void {
  try {
    if (wert === null) {
      speicher().removeItem(schluessel)
    } else {
      speicher().setItem(schluessel, wert)
    }
  } catch {
    // Ohne Speicher keine Wahl: das Portal bleibt im Heimat-Haus.
  }
}

const tab = () => sessionStorage
const browser = () => localStorage

/** Die Wahl dieses Tabs; ein neuer Tab uebernimmt einmalig die zuletzt getroffene. */
export function gewaehltesHaus(): string | null {
  const imTab = lies(tab, SCHLUESSEL_TAB)
  if (imTab) {
    return imTab
  }
  const zuletzt = lies(browser, SCHLUESSEL_ZULETZT)
  if (zuletzt) {
    schreib(tab, SCHLUESSEL_TAB, zuletzt)
  }
  return zuletzt
}

/**
 * Merkt die Wahl fuer diesen Tab und fuer neue Tabs. `null` = zurueck ins Heimat-Haus: dann geht gar
 * kein Header mehr mit, und das Portal verhaelt sich wie vor dem Haus-Wechsel.
 *
 * `konto` ist die Mailadresse, fuer die die Wahl gilt. Meldet sich auf demselben Rechner jemand
 * anderes an, verwirft `HausWechselService` sie, statt ihn ins Haus des Vorgaengers zu setzen.
 */
export function merkeHaus(hausId: string | null, konto: string | null = null): void {
  schreib(tab, SCHLUESSEL_TAB, hausId)
  schreib(browser, SCHLUESSEL_ZULETZT, hausId)
  schreib(browser, SCHLUESSEL_KONTO, hausId ? konto : null)
}

/** Fuer welches Konto die gemerkte Wahl gilt, `null` = unbekannt. */
export function kontoDerWahl(): string | null {
  return lies(browser, SCHLUESSEL_KONTO)
}

/** Haengt ein Haus vorn an „Zuletzt genutzt“ an. */
export function merkeVerlauf(hausId: string): void {
  schreib(browser, SCHLUESSEL_VERLAUF, JSON.stringify([hausId, ...zuletztGenutzt().filter((id) => id !== hausId)].slice(0, VERLAUF_LAENGE)))
}

/** Die zuletzt gewaehlten Haeuser, juengstes zuerst. Fuer „Zuletzt genutzt“ in langen Listen. */
export function zuletztGenutzt(): string[] {
  try {
    const wert: unknown = JSON.parse(lies(browser, SCHLUESSEL_VERLAUF) ?? '[]')
    return Array.isArray(wert) ? wert.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}
